import Flex from 'UIKit/Box/Flex'
import { FlexProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image/Image'
import Link from 'UIKit/Link'
import OpenEffect from 'UIKit/OpenEffect'
import Text from 'UIKit/Text/Text'
import Column from 'layout/Components/Column'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getIcon, Icons } from 'svgs'
import theme from 'theme'
import { CountListCurrencyDeposit, PaymentHeading } from 'views/Payment/MobilePayment/components/styles'

const BuyCryptoPlatformCard: React.FC<
  { logo: string; name: string; description: string; href: string } & FlexProps
> = ({ logo, name, description, href, ...props }) => {
  return (
    <Link disabledDefaultStyle href={href} external>
      <StyledPlatFormWrap {...props}>
        <RowCenter gap="12px">
          <RowCenter minHeight="32px" minWidth="32px">
            <Image className="logo" src={logo} width={32} height={32} />
          </RowCenter>

          <Flex flexDirection="column" gap="2px">
            <Text fontSize="14px" color="white" fontWeight={600} lineHeight="normal">
              {name}
            </Text>
            <Text color="var(--pm-text-note-color)" fontSize="12px" fontWeight={400} lineHeight="normal">
              {description}
            </Text>
          </Flex>
        </RowCenter>

        <Icons.ArrowDiagonalIcon width="20px" />
      </StyledPlatFormWrap>
    </Link>
  )
}

const NavigateDepositCard: React.FC<{ icon: string; name: string; description: string } & Omit<FlexProps, 'href'>> = ({
  icon,
  name,
  description,
  ...props
}) => {
  const Icon = icon && getIcon(icon)

  return (
    <StyledPlatFormWrap {...props}>
      <RowCenter gap="12px">
        <RowCenter minHeight="32px" minWidth="32px">
          {Icon && <Icon color={theme.colors.warning} width="32px" height="32px" />}
        </RowCenter>
        <Flex flexDirection="column" gap="2px">
          <Text fontSize="14px" color="white" fontWeight={600} lineHeight="normal">
            <Trans>{name}</Trans>
          </Text>
          <Text color="var(--pm-text-note-color)" fontSize="12px" fontWeight={400} lineHeight="normal">
            {description}
          </Text>
        </Flex>
      </RowCenter>

      <CountListCurrencyDeposit />
    </StyledPlatFormWrap>
  )
}

export const cardBuyCryptoes = [
  {
    logo: '/images/0x0.png',
    name: 'Moonpay',
    description: 'Apple Pay, Visa, Mastercard',
    href: 'https://buy.moonpay.com/',
  },
  {
    logo: '/images/paybis.png',
    name: 'Paybis',
    description: 'Visa, Mastercard',
    href: 'https://paybis.com/',
  },
  {
    logo: '/images/chainbis.png',
    name: 'Chainbits',
    description: 'Apple Pay | Card',
    href: 'https://buy.chainbits.com/',
  },
]

const DepositByBuyCrypto: React.FC<{ onNavigateToDeposit: () => void }> = ({ onNavigateToDeposit }) => {
  const { t } = useTranslation()

  return (
    <StyledContainer openType="grow" duration={0.4}>
      <Column gap={'40px'}>
        <PaymentHeading
          style={{
            textTransform: 'capitalize',
          }}
          display={['block', '', '', 'none']}
          fontWeight={700}
          fontSize="28px"
        >
          <Trans>Buy crypto</Trans>
        </PaymentHeading>

        <Column gap="32px">
          <Column gap="12px">
            <Column gap="var(--pm-space-label, 8px)">
              <PaymentHeading fontSize="18px">
                <Trans i18nKey="step_1_buy_crypto" />
              </PaymentHeading>
              <Text small color="var(--pm-text-note-color)">
                <Trans>Choose a trusted partner below to purchase crypto using your card</Trans>
              </Text>
            </Column>

            <Column gap="12px">
              {cardBuyCryptoes.map((card) => (
                <BuyCryptoPlatformCard
                  key={card.name}
                  logo={card.logo}
                  name={card.name}
                  description={card.description}
                  href={card.href}
                />
              ))}
            </Column>
          </Column>

          <Column gap="12px">
            <Column gap="var(--pm-space-label, 8px)">
              <PaymentHeading fontSize="18px">
                <Trans i18nKey="step_2_buy_crypto" />
              </PaymentHeading>
              <Text small color="var(--pm-text-note-color)">
                <Trans>Return to this page and transfer the crypto you just bought to your HunnyPlay Account</Trans>
              </Text>
            </Column>
            <RowMiddle>
              <NavigateDepositCard
                key={'Deposit Crypto'}
                icon="LightningIcon"
                name={t('Deposit Crypto')}
                description={`${t('No Fee')} • ${t('Instant')}`}
                onClick={onNavigateToDeposit}
              />
            </RowMiddle>
          </Column>
        </Column>
      </Column>
    </StyledContainer>
  )
}

const StyledContainer = styled(OpenEffect)`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mediaQueries.xs} {
    min-height: 450px;
  }
`
const StyledPlatFormWrap = styled(RowBetween)`
  max-height: 58px;
  border-width: 1px;
  border-style: solid;
  border-color: var(--pm-border);
  border-radius: var(--pm-trigger-radius);
  padding: 12px;
  padding-right: 18px;
  background-color: var(--pm-card);
  cursor: pointer;
  width: 100%;

  .logo {
    transform: scale(1.1);
  }

  &:hover {
    background-color: var(--pm-card-active);
    ${Text} {
      color: #fff;
    }
  }
`

export default DepositByBuyCrypto
